// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { Card, CbhIcon, Rating, Text, Title } from "@clipboard-health/ui-components";
import {
  Avatar,
  CardContent,
  Checkbox,
  Collapse,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  type Theme,
} from "@mui/material";
import { hideKeyboard } from "@src/appV2/lib/utils/capacitor/hideKeyboard";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { useIsWorkplaceReviewUXUpdateEnabled } from "../hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { type PostShiftReviewFormValues, type StepAction } from "../types";

const REVIEW_LENGTH_CONFIG = {
  old: { minLength: 10, minLengthDelayed: 10, maxLength: 300, maxLengthDelayed: 1000 },
  new: { minLength: 1, minLengthDelayed: 1, maxLength: 600, maxLengthDelayed: 600 },
} as const;

export interface WriteReviewStepProps {
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
  isDelayedReview?: boolean;
}

function getCoachingContent(effectiveLength: number) {
  if (effectiveLength >= 400) {
    return {
      icon: "🙌",
      title: "This is as good as it gets",
      subtitle: "You're in the top 5% of reviewers. Seriously, thank you.",
      backgroundColor: (theme: Theme) => theme.palette.intent?.success.background,
      progressColor: (theme: Theme) => theme.palette.intent?.success.text,
    };
  }

  if (effectiveLength >= 200) {
    return {
      icon: "⭐",
      title: "Top tier review",
      subtitle: "Your peers will love this.",
      backgroundColor: (theme: Theme) => theme.palette.shift?.block.background,
      progressColor: (theme: Theme) => theme.palette.shift?.block.text,
    };
  }
  if (effectiveLength >= 100) {
    return {
      icon: "👍",
      title: "Professionals like you help others show up prepared",
      subtitle: "Get to 200 to be in the top 15% of reviews.",
      backgroundColor: (theme: Theme) => theme.palette.shift?.priority.background,
      progressColor: (theme: Theme) => theme.palette.shift?.priority.text,
    };
  }

  if (effectiveLength >= 50) {
    return {
      icon: "🔥",
      title: "Nearly there",
      subtitle: `Just ${100 - effectiveLength} more ${100 - effectiveLength === 1 ? "character" : "characters"} to unlock that 3× boost.`,
      backgroundColor: (theme: Theme) => theme.palette.intent?.warning.background,
      progressColor: (theme: Theme) => theme.palette.intent?.warning.text,
    };
  }

  return {
    icon: "chat" as const,
    title: "Longer reviews get more engagement",
    subtitle: "Reviews over 100 characters get 3× more likes and 4× more replies.",
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
    progressColor: (theme: Theme) => theme.palette.text.tertiary,
  };
}

function getNewUiConfig(isDelayedReview: boolean) {
  const lengths = REVIEW_LENGTH_CONFIG.new;

  if (isDelayedReview) {
    return {
      title: "Tell others about this workplace",
      titleAlign: undefined,
      placeholder: "Share a story about your experience...",
      disclaimer:
        "Reviews that use inappropriate language or certain references to patients and specific workplace personnel will be removed.",
      minLength: lengths.minLengthDelayed,
      maxLength: lengths.maxLengthDelayed,
      rows: 6,
      trimTrailing: true,
    };
  }

  return {
    title: "Leave your thoughts",
    titleAlign: "center" as const,
    placeholder:
      "Please review the shift and the workplace. What should a new worker at this workplace know?",
    disclaimer:
      "Reviews that use inappropriate language or certain references to patients and specific facility personnel will be removed. Repeat offenders may be banned from the marketplace",
    minLength: lengths.minLength,
    maxLength: lengths.maxLength,
    rows: 6,
    trimTrailing: true,
  };
}

function NewWriteReviewStepUi({
  isSubmitting,
  onAction,
  isDelayedReview,
}: {
  isSubmitting: boolean;
  onAction: (action: StepAction) => void;
  isDelayedReview: boolean;
}) {
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();
  const reviewText = watch("reviewText");
  const isKeyboardOpen = useIsKeyboardOpen();
  const [showExample, setShowExample] = useState(false);

  const config = getNewUiConfig(isDelayedReview);
  const effectiveLength = config.trimTrailing ? reviewText.trimEnd().length : reviewText.length;
  const isValid = effectiveLength >= config.minLength && effectiveLength <= config.maxLength;
  const isOverLimit = effectiveLength > config.maxLength;

  const coaching = getCoachingContent(effectiveLength);
  const progressPercent = Math.min((effectiveLength / config.maxLength) * 100, 100);
  const progressColor = isOverLimit ? "error.main" : coaching.progressColor;

  return (
    <>
      <DialogContent sx={{ py: 2, display: "flex", flexDirection: "column", gap: 6 }}>
        {!isKeyboardOpen && (
          <Title variant="h3" component="h3" textAlign={config.titleAlign}>
            {config.title}
          </Title>
        )}

        <Stack spacing={4}>
          <Card outlined sx={{ backgroundColor: coaching.backgroundColor }}>
            <CardContent sx={{ py: 6, px: 6 }}>
              <Stack direction="row" spacing={3} alignItems="flex-start">
                {coaching.icon === "chat" ? (
                  <CbhIcon type="chat" size="medium" />
                ) : (
                  <Text variant="body1">{coaching.icon}</Text>
                )}
                <Stack spacing={1}>
                  <Text bold variant="body2">
                    {coaching.title}
                  </Text>
                  <Text variant="body2" color="text.secondary">
                    {coaching.subtitle}
                  </Text>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={1}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: progressColor,
                },
              }}
            />
            <Text
              variant="caption"
              color={
                effectiveLength >= 50 || isOverLimit
                  ? progressColor
                  : (theme: Theme) => theme.palette.text.secondary
              }
              textAlign="right"
            >
              {effectiveLength} / {config.maxLength}
            </Text>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              if (!showExample && isKeyboardOpen) {
                void hideKeyboard();
              }

              setShowExample(!showExample);
            }}
          >
            <CbhIcon type="info" size="small" />
            <Text variant="body2" sx={{ textDecoration: "underline" }}>
              {showExample ? "Hide example" : "See what a great review looks like"}
            </Text>
          </Stack>

          <Collapse in={showExample && !isKeyboardOpen}>
            <Card outlined>
              <CardContent sx={{ p: 5 }}>
                <Stack spacing={3}>
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main", fontSize: 14 }}>
                      IR
                    </Avatar>
                    <Stack sx={{ flex: 1 }}>
                      <Text bold variant="body2">
                        Ivana Ramirez
                      </Text>
                      <Text variant="caption" color="text.secondary">
                        LVN
                      </Text>
                    </Stack>
                    <Rating readOnly value={3} size="small" />
                  </Stack>
                  <Text variant="body2">
                    Mixed experience. The supply cart on 3N was well-stocked — gloves, gauze,
                    everything you need. But staffing was thin on my Saturday shift, 1:9 ratio,
                    which made it hard to give patients the time they needed. Staff were friendly
                    enough, charge nurse was responsive. I&apos;d go back for a day shift but
                    wouldn&apos;t do weekends again.
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          </Collapse>

          <TextField
            multiline
            rows={config.rows}
            placeholder={config.placeholder}
            value={reviewText}
            disabled={isSubmitting}
            error={isOverLimit}
            onChange={(event) => {
              setValue("reviewText", event.target.value, { shouldValidate: true });
            }}
          />
        </Stack>

        <Text variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
          {config.disclaimer}
        </Text>
      </DialogContent>
      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isSubmitting}
            disabled={!isValid}
            onClick={() => {
              onAction({
                type: "NEXT_STEP",
                data: { reviewText },
              });
            }}
          >
            Continue
          </LoadingButton>
          {!isSubmitting && (
            <TextButton
              sx={{ alignSelf: "center" }}
              onClick={() => {
                setValue("reviewText", "");
                setValue("isAnonymous", false);
                onAction({ type: "SUBMIT_REVIEW" });
              }}
            >
              Skip
            </TextButton>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}

function LegacyWriteReviewStepUi({
  isSubmitting,
  onAction,
  isDelayedReview,
}: {
  isSubmitting: boolean;
  onAction: (action: StepAction) => void;
  isDelayedReview: boolean;
}) {
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();
  const reviewText = watch("reviewText");
  const isAnonymous = watch("isAnonymous");

  const lengths = REVIEW_LENGTH_CONFIG.old;
  const minLength = isDelayedReview ? lengths.minLengthDelayed : lengths.minLength;
  const maxLength = isDelayedReview ? lengths.maxLengthDelayed : lengths.maxLength;
  const effectiveLength = isDelayedReview ? reviewText.trimEnd().length : reviewText.length;
  const isValid = effectiveLength >= minLength && reviewText.length <= maxLength;
  const isOverLimit = reviewText.length > maxLength;

  function getHelperText() {
    if (isOverLimit) {
      return `${reviewText.length}/${maxLength} characters (too long)`;
    }

    if (effectiveLength < minLength) {
      return `${effectiveLength}/${maxLength} characters (${
        minLength - effectiveLength
      } more needed)`;
    }

    return `${effectiveLength}/${maxLength} characters`;
  }

  const title = "Leave your thoughts";
  const subtitle = isDelayedReview
    ? "Share details about the workplace and the shift to help other professionals know what to expect."
    : undefined;
  const placeholder = isDelayedReview
    ? "Share details about your experience — what worked well, what didn't, or anything worth noting."
    : "Please review the shift and the workplace. What should a new worker at this workplace know?";
  const disclaimer = isDelayedReview
    ? "Reviews that use inappropriate language or certain references to patients and specific workplace personnel will be removed. Repeated violations may impact your account."
    : "Reviews that use inappropriate language or certain references to patients and specific facility personnel will be removed. Repeat offenders may be banned from the marketplace";

  return (
    <>
      <DialogContent sx={{ py: 2, display: "flex", flexDirection: "column", gap: 6 }}>
        <Stack spacing={3}>
          <Title variant="h3" component="h3" textAlign="center">
            {title}
          </Title>
          {subtitle && (
            <Text variant="body2" textAlign="center" color="text.secondary">
              {subtitle}
            </Text>
          )}
        </Stack>

        <Stack spacing={4}>
          <TextField
            multiline
            rows={8}
            placeholder={placeholder}
            value={reviewText}
            disabled={isSubmitting}
            error={isOverLimit}
            helperText={getHelperText()}
            sx={{ pt: 5 }}
            onChange={(event) => {
              setValue("reviewText", event.target.value, { shouldValidate: true });
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous ?? false}
                disabled={isSubmitting}
                onChange={(event) => {
                  setValue("isAnonymous", event.target.checked, { shouldValidate: true });
                }}
              />
            }
            label={<Text>I want my review to be anonymous</Text>}
          />
        </Stack>

        <Text variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
          {disclaimer}
        </Text>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          isLoading={isSubmitting}
          disabled={!isValid}
          onClick={() => {
            onAction({
              type: "NEXT_STEP",
              data: { reviewText },
            });
          }}
        >
          Continue
        </LoadingButton>
      </DialogFooter>
    </>
  );
}

export function WriteReviewStep(props: WriteReviewStepProps) {
  const { isSubmitting = false, onAction, isDelayedReview = false } = props;
  const { isEnabled: isNewUi } = useIsWorkplaceReviewUXUpdateEnabled();

  if (isNewUi) {
    return (
      <NewWriteReviewStepUi
        isSubmitting={isSubmitting}
        isDelayedReview={isDelayedReview}
        onAction={onAction}
      />
    );
  }

  return (
    <LegacyWriteReviewStepUi
      isSubmitting={isSubmitting}
      isDelayedReview={isDelayedReview}
      onAction={onAction}
    />
  );
}
